import React from "react";
import { Dropdown, Option } from "@campgladiator/cgui-core.atoms.dropdown";
import { Paragraph } from "@campgladiator/cgui-core.atoms.typography";
import { UpgradeMembershipModalProps } from "../../UpgradeMembershipModal";
import { ProductHeading } from "../ProductHeading";
import {
  calculateDiscount,
  getActiveMembershipPrice,
} from "@campgladiator/cg-common.logic.memberships";
import { extractDecimalNumber } from "@campgladiator/cg-common.utility.numbers";
import { UpgradeType } from "@campgladiator/cg-common.types.types";
import { SelectCouponInput } from "../../../SelectCouponInput";
import { setInitialValueIfOnlyOneOption } from "../../../../utils";
import { SelectedCouponOption } from "../../../SelectCouponInput/useSelectCouponInput";
import { SelectedProductOption } from "./useSelectProduct";
import styles from "./SelectProduct.module.scss";

export type SelectProductProps = {
  couponOptions: Option[];
  handleSelectCouponOption: (option?: Option) => void;
  handleSelectProductOption: (option?: Option) => void;
  isCouponStepVisible?: boolean;
  productOptions: Option[];
  selectedCouponOption: SelectedCouponOption | null;
  selectedProductOption: SelectedProductOption | null;
  upgradeType: UpgradeType;
} & Pick<UpgradeMembershipModalProps, "productName" | "productPrice">;

const SelectProduct = ({
  productOptions,
  upgradeType,
  couponOptions,
  productName,
  productPrice,
  handleSelectCouponOption,
  handleSelectProductOption,
  selectedCouponOption,
  selectedProductOption,
  isCouponStepVisible,
}: SelectProductProps) => {
  const selectedProductPrice = selectedProductOption
    ? getActiveMembershipPrice(selectedProductOption.productDetails.prices)
    : "0";
  const discount =
    selectedCouponOption && selectedProductOption
      ? calculateDiscount(
          extractDecimalNumber(selectedProductPrice),
          selectedCouponOption.percentageDiscount,
          selectedCouponOption.duration,
          selectedProductOption.productDetails.termLength,
          selectedCouponOption.amountOff
        )
      : null;

  const upgradeTypeLabel =
    upgradeType === "UPGRADE" ? "Change Term To" : "Swap to";
  const hasSelectedCouponOptionAndDiscount = selectedCouponOption && discount;

  return (
    <>
      <ProductHeading productName={productName} productPrice={productPrice} />
      <div className={styles.upgradeForm}>
        <Paragraph size="small" className={styles.label}>
          {upgradeTypeLabel}
        </Paragraph>
        <Dropdown
          options={setInitialValueIfOnlyOneOption(productOptions)}
          disabled={!productOptions.length}
          onChangeSelectedOption={handleSelectProductOption}
        />
      </div>
      {isCouponStepVisible && (
        <div className={styles.upgradeForm}>
          <SelectCouponInput
            couponOptions={couponOptions}
            handleSelectCouponOption={handleSelectCouponOption}
            isDisabled={!selectedProductOption}
          />
        </div>
      )}
      {hasSelectedCouponOptionAndDiscount && (
        <div className={styles.summary}>
          <Paragraph size="small" className={styles.couponLabel}>
            Coupon applied
          </Paragraph>
          <Paragraph size="small" className={styles.couponLabel}>
            -${discount.discountAmount}
          </Paragraph>
        </div>
      )}
      {selectedProductOption && (
        <div className={styles.summary}>
          <Paragraph size="small" className={styles.label}>
            Total due {discount ? "today" : ""}
          </Paragraph>
          <Paragraph size="small" className={styles.label}>
            {discount ? `$${discount.newPrice}` : selectedProductPrice}/mo
          </Paragraph>
        </div>
      )}
    </>
  );
};

export default SelectProduct;
